import React, { useState, useEffect } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ProductCard } from '@/components/common/ProductCard';
import { getScanHistory } from '@/lib/api';
import { Search, History, Loader2, Scan } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const data = await getScanHistory();
        setHistory(data);
        setFilteredHistory(data);
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  // Filter history based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredHistory(history);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = history.filter(item => 
        item.product_name.toLowerCase().includes(query) ||
        item.barcode?.toLowerCase().includes(query)
      );
      setFilteredHistory(filtered);
    }
  }, [searchQuery, history]);

  return (
    <div className="min-h-screen bg-background" data-testid="history-page">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Scan History
            </h1>
            <p className="text-muted-foreground text-lg">
              View all your previously scanned products
            </p>
          </div>
          
          {/* Search */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="history-search-input"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredHistory.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="history-list">
            {filteredHistory.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : history.length > 0 ? (
          <Card className="border border-dashed border-border">
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No results found</h3>
              <p className="text-muted-foreground">
                No products match "{searchQuery}"
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-dashed border-border">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <History className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No scan history yet</h3>
              <p className="text-muted-foreground mb-6">
                Start scanning products to build your history
              </p>
              <Link to="/scan">
                <Button data-testid="start-scanning-btn">
                  <Scan className="w-4 h-4 mr-2" />
                  Start Scanning
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Stats summary */}
        {history.length > 0 && (
          <div className="mt-8 p-6 bg-muted/50 rounded-xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold">{history.length}</p>
                <p className="text-sm text-muted-foreground">Total Scans</p>
              </div>
              <div>
                <p className="text-3xl font-bold">
                  {Math.round(history.reduce((sum, h) => sum + h.health_score, 0) / history.length)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Score</p>
              </div>
              <div>
                <p className="text-3xl font-bold">
                  {history.reduce((sum, h) => sum + (h.allergen_warnings?.length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Allergens Found</p>
              </div>
              <div>
                <p className="text-3xl font-bold">
                  {history.reduce((sum, h) => sum + (h.hidden_ingredients?.length || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Hidden Ingredients</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HistoryPage;
